import { useIonRouter, useIonViewWillEnter } from "@ionic/react";
import dayjs from "dayjs";
import { FC, ReactElement, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Redirect } from "react-router-dom";
import styled from "styled-components";
import Info from "../../components/atoms/ErrorComponent";
import ResonanceLoader from "../../components/atoms/ResonanceLoader/ResonanceLoader";
import ResonanceLayout from "../../components/organisms/ResonanceContext/ResonanceLayout";
import { useResonanceLayout } from "../../components/organisms/ResonanceContext/ResonanceLayoutContext";
import { useResonanceData } from "../../components/organisms/ResonanceContext/ResonanceDataContext";
import { useConfig } from "../../contexts/ConfigContext";
import { useStudySlug } from "../../hooks/useStudySlug";
import { getDefaultTabPath } from "../../navigation/studyTabs";
import {
  CalendarUpcomingWindow,
  getUpcomingWindows,
} from "../../services/calendar.service";
import { RoleEnum } from "../../types/invitation.types";
import { SequenceType } from "../../types/Questionnaire";
import { DueChip, EmptyText, ListCard, Page, Row, RowEnd, RowMain, RowSub, RowTitle, SectionLabel, Chevron } from "../Suivi/suiviUi";

/**
 * Subject «Calendrier» — agenda of the participant's own questionnaires,
 * grouped by month (approved design: agenda list, no month grid):
 *   - OPEN entries: questionnaires to do now — deadline + urgency chip,
 *     tappable straight into the runner (returns here on finish);
 *   - UPCOMING entries: future windows («S'ouvre le …») from the
 *     upcoming_windows endpoint (the cron's own schedule math);
 *   - RECURRENT entries: next occurrence of recurring questionnaires
 *     (nextEligibleAt from the availability data already in context).
 * Subject-only (tab is role-gated; the page self-guards for deep links).
 */

type AgendaEntry = {
  key: string;
  kind: "OPEN" | "UPCOMING" | "RECURRENT";
  date: string;
  label: string | null;
  /** OPEN only: runner target. */
  userSequenceId?: number;
  componentId?: number;
};

const SubjectCalendar: FC = (): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const ionRouter = useIonRouter();
  const study_slug = useStudySlug();
  const { study, selectedRole } = useConfig();
  const { setResonanceLayout } = useResonanceLayout();
  const { sequences, sequenceAvailabilityBySequenceId } = useResonanceData();

  const [upcoming, setUpcoming] = useState<CalendarUpcomingWindow[] | null>(null);
  const [isError, setIsError] = useState(false);

  const fetchUpcoming = useCallback((): void => {
    if (!study?.id) return;
    // Never fire while the self-guard below is redirecting a non-subject away.
    if (selectedRole && selectedRole.name !== RoleEnum.SUBJECT) return;
    getUpcomingWindows({ studyId: study.id, lang: language })
      .then(({ data }) => {
        setIsError(false);
        setUpcoming(data.upcoming);
      })
      .catch(() => setIsError(true));
  }, [study?.id, language, selectedRole]);

  // Refetch on every tab entry (trampoline — useIonViewWillEnter registers once).
  const willEnterRef = useRef<(() => void) | undefined>(undefined);
  willEnterRef.current = () => {
    setResonanceLayout({
      disableHeader: false,
      disableNav: false,
      backgroundColor: "white",
      title: "calendar_tab_label",
      welcomePage: false,
      headerLeft: undefined, // project default (logo, or Mental+'s burger)
    });
    fetchUpcoming();
  };
  useIonViewWillEnter(() => {
    willEnterRef.current?.();
  });

  // In-place language change never re-enters the view — keep labels live.
  useEffect(() => {
    fetchUpcoming();
  }, [fetchUpcoming]);

  if (selectedRole && selectedRole.name !== RoleEnum.SUBJECT) {
    return <Redirect to={getDefaultTabPath(study_slug, selectedRole.name)} />;
  }

  // --- Assemble the agenda ---------------------------------------------------

  const openEntries: AgendaEntry[] = (sequences ?? [])
    .filter((seq: SequenceType) => seq.status !== "DONE")
    .map((seq: SequenceType): AgendaEntry | null => {
      // Same target selection as the Questionnaires tab: first not-done
      // questionnaire component of the user-sequence.
      const next = seq.components.find(
        (component) => component.questionnaire_id && !component.is_done
      );
      if (!next) return null;
      return {
        key: `open-${seq.id}`,
        kind: "OPEN",
        date: String(seq.end_date),
        // Child context for about-someone-else occurrences («Fiche enfant — Emma»).
        label: seq.subjectDisplayLabel
          ? `${seq.label} — ${seq.subjectDisplayLabel}`
          : seq.label,
        userSequenceId: seq.id,
        componentId: next.id,
      };
    })
    .filter((entry): entry is AgendaEntry => entry != null);

  // Occurrence-level keys: one sequence can be OPEN about child A while still UPCOMING
  // about child B — dedupe per (sequence, subject), not per sequence. Plain sequence ids
  // kept as fallback for API payloads without subject_user_id.
  const openOccurrenceKeys = new Set(
    (sequences ?? []).map((seq) => `${seq.sequence_id}:${seq.subject?.id ?? "self"}`)
  );
  const openSequenceIds = new Set((sequences ?? []).map((seq) => seq.sequence_id));
  const labelBySequenceId = new Map<number, string | null>(
    (sequences ?? []).map((seq) => [seq.sequence_id, seq.label])
  );

  const recurrentEntries: AgendaEntry[] = Object.values(
    sequenceAvailabilityBySequenceId ?? {}
  )
    .filter(
      (availability) =>
        availability.isRecurrent &&
        !availability.isAvailableNow &&
        availability.nextEligibleAt &&
        // Future dates only: a bounded recurrent whose window expired keeps a
        // frozen past nextEligibleAt forever (the cron stops recreating it) —
        // never show an occurrence the cron won't materialize.
        dayjs(availability.nextEligibleAt).endOf("day").isAfter(dayjs())
    )
    .map((availability) => ({
      key: `recurrent-${availability.sequenceId}`,
      kind: "RECURRENT" as const,
      date: String(availability.nextEligibleAt),
      label: labelBySequenceId.get(availability.sequenceId) ?? null,
    }));

  const upcomingEntries: AgendaEntry[] = (upcoming ?? [])
    // A recurrent "next" and an upcoming window can't overlap (the endpoint is
    // non-recurrent only), but a window whose occurrence is already OPEN would be
    // stale context vs fresh fetch — dedupe defensively, per (sequence, subject).
    .filter((window) =>
      window.subject_user_id != null
        ? !openOccurrenceKeys.has(`${window.sequence_id}:${window.subject_user_id}`)
        : !openSequenceIds.has(window.sequence_id)
    )
    .map((window) => ({
      key: `upcoming-${window.sequence_id}-${window.subject_user_id ?? "self"}`,
      kind: "UPCOMING" as const,
      date: window.opens_at,
      label: window.subject_label
        ? `${window.label} — ${window.subject_label}`
        : window.label,
    }));

  const entries = [...openEntries, ...upcomingEntries, ...recurrentEntries].sort(
    (first, second) => new Date(first.date).getTime() - new Date(second.date).getTime()
  );

  // Group by month, labeled in the app language («Juillet 2026»).
  const monthLabel = (date: string): string => {
    const formatted = new Intl.DateTimeFormat(language, {
      month: "long",
      year: "numeric",
    }).format(new Date(date));
    return formatted.charAt(0).toUpperCase() + formatted.slice(1);
  };
  const months: { key: string; label: string; entries: AgendaEntry[] }[] = [];
  entries.forEach((entry) => {
    const key = dayjs(entry.date).format("YYYY-MM");
    let month = months.find((candidate) => candidate.key === key);
    if (!month) {
      month = { key, label: monthLabel(entry.date), entries: [] };
      months.push(month);
    }
    month.entries.push(entry);
  });

  const openRunner = (entry: AgendaEntry): void => {
    if (!entry.userSequenceId || !entry.componentId) return;
    ionRouter.push(
      `/${study_slug}/tabs/calendar/sequences/${entry.userSequenceId}/components/${entry.componentId}`,
      "forward",
      "push"
    );
  };

  const isLoading = upcoming === null && !isError;

  return (
    <ResonanceLayout>
      <Page>
        {isError ? (
          // Full error page (SuiviHome contract): an agenda missing its upcoming
          // half must never masquerade as "nothing planned".
          <Info
            title={t("common_server_error_title")}
            description={t("common_server_error_desc")}
          />
        ) : months.length === 0 && isLoading ? (
          <ResonanceLoader />
        ) : months.length === 0 ? (
          <EmptyText>{t("calendar_empty")}</EmptyText>
        ) : (
          months.map((month) => (
            <div key={month.key}>
              <SectionLabel>{month.label}</SectionLabel>
              <ListCard>
                {month.entries.map((entry) => (
                  <Row
                    key={entry.key}
                    as={entry.kind === "OPEN" ? "button" : undefined}
                    type={entry.kind === "OPEN" ? "button" : undefined}
                    $tappable={entry.kind === "OPEN"}
                    onClick={entry.kind === "OPEN" ? () => openRunner(entry) : undefined}
                  >
                    <DayBadge $active={entry.kind === "OPEN"}>
                      {dayjs(entry.date).format("DD")}
                    </DayBadge>
                    <RowMain>
                      <RowTitle>{entry.label ?? t("suivi_questionnaire")}</RowTitle>
                      <RowSub>
                        {entry.kind === "OPEN" &&
                          t("calendar_due_before", {
                            date: dayjs(entry.date).format("DD/MM"),
                          })}
                        {entry.kind === "UPCOMING" &&
                          t("calendar_opens_on", {
                            date: dayjs(entry.date).format("DD/MM"),
                          })}
                        {entry.kind === "RECURRENT" &&
                          t("calendar_next_time", {
                            date: dayjs(entry.date).format("DD/MM"),
                          })}
                      </RowSub>
                    </RowMain>
                    {entry.kind === "OPEN" && (
                      <RowEnd>
                        <DueChip endDate={entry.date} t={t} />
                      </RowEnd>
                    )}
                    {entry.kind === "OPEN" && <Chevron>›</Chevron>}
                  </Row>
                ))}
              </ListCard>
            </div>
          ))
        )}
      </Page>
    </ResonanceLayout>
  );
};

/* Leading day-of-month badge — quiet square, accented for actionable entries. */
const DayBadge = styled.span<{ $active?: boolean }>`
  width: 34px;
  height: 34px;
  border-radius: 10px;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  background: ${({ $active }) =>
    $active
      ? "rgba(var(--app-color-primary-rgb, 37, 99, 235), 0.1)"
      : "rgba(var(--app-color-text-rgb, 15, 23, 42), 0.05)"};
  color: ${({ $active }) =>
    $active
      ? "var(--app-color-primary, #2563eb)"
      : "rgba(var(--app-color-text-rgb, 15, 23, 42), 0.6)"};
`;

export default SubjectCalendar;
